import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = ({ containerRef, excludeRoutes = [] }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Check if current route should be excluded
    const shouldExclude = excludeRoutes.some(route => pathname === route);
    
    if (shouldExclude) {
      console.log(`ScrollToTop: Skipping scroll reset for route: ${pathname}`);
      return;
    }

    console.log(`ScrollToTop: Resetting scroll for route: ${pathname}`);

    // AGGRESSIVE MULTI-METHOD SCROLL RESET
    const aggressiveScrollReset = () => {
      // Method 1: Direct container manipulation
      if (containerRef.current) {
        // Force immediate scroll position
        containerRef.current.scrollTop = 0;
        containerRef.current.scrollLeft = 0;
        
        // Try the scrollTo method
        if (containerRef.current.scrollTo) {
          containerRef.current.scrollTo(0, 0);
          containerRef.current.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
        
        // CSS transform reset (locomotive sometimes uses transforms)
        const locomotiveContainer = containerRef.current.querySelector('[data-scroll-container]');
        if (locomotiveContainer) {
          locomotiveContainer.style.transform = 'translate3d(0px, 0px, 0px)';
        }
      }

      // Method 2: Window and document scroll reset
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.body.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
      // Method 3: Force document scroll properties
      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
      window.pageYOffset = 0;
      window.pageXOffset = 0;

      // Method 4: Try to access locomotive scroll instance directly
      try {
        // Check if locomotive scroll instance exists on window
        if (window.locomotive) {
          window.locomotive.scrollTo(0, { duration: 0, disableLerp: true });
        }
        
        // Alternative: check for locomotive on the container
        if (containerRef.current && containerRef.current.locomotive) {
          containerRef.current.locomotive.scrollTo(0, { duration: 0, disableLerp: true });
        }
      } catch (error) {
        console.log('Locomotive scroll instance not found, using fallback methods');
      }

      console.log(`Scroll reset applied for: ${pathname}`);
    };

    // Execute immediately
    aggressiveScrollReset();

    // Execute with progressive delays to catch locomotive initialization
    const timeouts = [];
    [10, 50, 100, 200, 300, 500, 800].forEach(delay => {
      timeouts.push(setTimeout(aggressiveScrollReset, delay));
    });

    // Cleanup
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [pathname, containerRef, excludeRoutes]);

  return null;
};

export default ScrollToTop;
